import * as React from "react";
import { useMemo } from "react";
import { Case } from "../../models/Case";
import { DeleteCaseRequest } from "../../models/Jobs/JobRequest";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { JobType } from "../../models/Jobs/JobType";
import { JobEntityType } from "../../models/Jobs/JobEntityType";
import { ActiveCaseJobsExplorer } from "../../pages/CasesPage";
import { UserRole } from "../../models/User";
import { MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS } from "../common/ConfirmMigrationEntityBlockUpdateModal";
import CasesRepository from "../../repositories/CasesRepository";
import ConfirmCaseFlagUpdateModal from "./ConfirmCaseFlagUpdateModal";
import MigrationEntityTable, { type MigrationEntityTableConfig } from "../common/MigrationEntityTable/MigrationEntityTable";
import { QUERY_KEYS } from "../../constants/QueryKeys";

interface CasesTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveCaseJobsExplorer;
  newCasesForDeletion: DeleteCaseRequest[];
  setNewCasesForDeletion: React.Dispatch<React.SetStateAction<DeleteCaseRequest[]>>;
  newCasesForExport: Case[];
  setNewCasesForExport: React.Dispatch<React.SetStateAction<Case[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CasesTable: React.FC<CasesTableProps> = ({
  organizationId,
  activeJobsExplorer,
  newCasesForDeletion,
  setNewCasesForDeletion,
  newCasesForExport,
  setNewCasesForExport,
  userRole,
  isAssignedUser,
}) => {
  const casesRepository = new CasesRepository();

  const config = useMemo<MigrationEntityTableConfig<Case>>(() => ({
    entityName: "cases",
    searchLabel: "Search cases",
    searchPlaceholder: "Case number",
    emptyMessage: "No cases found.",
    entityKeySortProperty: "caseNumber",
    // Blocked cases first, then Acknowledged: a case a flag holds is what an operator opens this list to find.
    defaultSort: "flagStatus",
    flagStatusSortProperty: "flagStatus",
    queryKey: QUERY_KEYS.CASES,
    detailQueryKey: QUERY_KEYS.CASE,

    getEntityKey: (c) => c.caseNumber,
    getDetailPath: (orgId, entityId) => `/organization/${orgId}/case/${entityId}`,

    fetchEntities: (params) => casesRepository.getCases({
      organizationId: params.organizationId,
      caseNumber: params.search,
      descending: params.descending,
      orderBy: params.orderBy,
      status: params.status,
      isLocked: params.isLocked,
      hasFlaggedPaths: params.hasFlaggedPaths,
      hasConflicts: params.hasConflicts,
      flagStatus: params.flagStatus,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }),

    deleteExplorerValue: ActiveCaseJobsExplorer.DeleteCases,
    exportExplorerValue: ActiveCaseJobsExplorer.ExportCsv,

    createDeleteRequest: (entity, orgId) => ({
      migrationEntityId: entity.id,
      caseNumber: entity.caseNumber,
      status: JobRequestStatus.New,
      type: JobType.Delete,
      entityType: JobEntityType.Case,
      organizationId: orgId,
    } as DeleteCaseRequest),

    updateFlag: (entityId, flagStatus) => casesRepository.updateCaseFlag(entityId, flagStatus),
    updateBlock: (entityId, isBlocked) => casesRepository.updateCaseBlock(entityId, isBlocked),
    blockConfig: MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS.CASE,

    renderFlagModal: ({ open, entityKey, flagStatus, onClose, onConfirm }) => (
      <ConfirmCaseFlagUpdateModal
        open={open}
        caseNumber={entityKey}
        flagStatus={flagStatus}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    ),
  }), [casesRepository]);

  return (
    <MigrationEntityTable<Case>
      config={config}
      organizationId={organizationId}
      activeJobsExplorer={activeJobsExplorer}
      newForDeletion={newCasesForDeletion}
      setNewForDeletion={setNewCasesForDeletion}
      newForExport={newCasesForExport}
      setNewForExport={setNewCasesForExport}
      userRole={userRole}
      isAssignedUser={isAssignedUser}
    />
  );
};

export default CasesTable;
